import { type StringKey } from './i18n';
import type { CategoryCode } from './types';

/**
 * 10 universal categories rendered in the per-receipt dropdown. The order
 * here drives the dropdown order — common ones first. Labels come from
 * `lib/i18n.ts` keyed by `category.<code>`.
 */
export const CATEGORY_CODES: readonly CategoryCode[] = [
  'food',
  'transport',
  'office',
  'healthcare',
  'travel',
  'utilities',
  'entertainment',
  'subscriptions',
  'personal',
  'other',
] as const;

/**
 * Stable English labels for exports. The UI shows translated labels via
 * `CATEGORY_LABEL_KEYS` + i18n, but CSV/XLSX headers and values stay
 * English for consistency with the rest of the export schema and so a
 * file shared with a bookkeeper / accountant in another country reads
 * the same regardless of who exported it.
 */
export const CATEGORY_EXPORT_LABELS: Record<CategoryCode, string> = {
  food: 'Food',
  transport: 'Transport',
  office: 'Office',
  healthcare: 'Healthcare',
  travel: 'Travel',
  utilities: 'Utilities',
  entertainment: 'Entertainment',
  subscriptions: 'Subscriptions',
  personal: 'Personal',
  other: 'Other',
};

export const CATEGORY_LABEL_KEYS: Record<CategoryCode, StringKey> = {
  food: 'category.food',
  transport: 'category.transport',
  office: 'category.office',
  healthcare: 'category.healthcare',
  travel: 'category.travel',
  utilities: 'category.utilities',
  entertainment: 'category.entertainment',
  subscriptions: 'category.subscriptions',
  personal: 'category.personal',
  other: 'category.other',
};

/**
 * Vendor → category memory in localStorage. When the user picks a category
 * for a receipt, we remember it keyed by lowercased+trimmed vendor name so
 * the next receipt from the same vendor pre-fills the dropdown. Cheap
 * stickiness for users running the same restaurant / supplier through
 * the app over and over.
 */
const STORAGE_KEY = 'rr-vendor-categories';

function vendorKey(vendor: string): string {
  return vendor.trim().toLowerCase();
}

function loadMap(): Record<string, CategoryCode> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed as Record<string, CategoryCode>;
  } catch {
    return {};
  }
}

function saveMap(map: Record<string, CategoryCode>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage unavailable — auto-suggest just won't fire next time.
  }
}

export function getVendorCategory(vendor: string): CategoryCode | undefined {
  const key = vendorKey(vendor);
  if (key === '') return undefined;
  return loadMap()[key];
}

export function rememberVendorCategory(
  vendor: string,
  category: CategoryCode,
): void {
  const key = vendorKey(vendor);
  if (key === '') return;
  const map = loadMap();
  map[key] = category;
  saveMap(map);
}
